import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Chip,
} from '@mui/material';
import {
  savePrompt,
  updatePrompt,
  updateDefaultPrompt,
  SystemPrompt,
  SaveSystemPromptParams,
} from '../../services/promptService';
import { useNotification } from '../../contexts/NotificationContext';
import { SUB_FEATURES_CONFIG } from './index';

interface Snippet {
  label: string;
  value: string;
}

interface PromptEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  prompt?: SystemPrompt;
  feature: string;
}

export const PromptEditModal: React.FC<PromptEditModalProps> = ({
  open,
  onClose,
  onSave,
  prompt,
  feature,
}) => {
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  const [name, setName] = useState('');
  const [promptType, setPromptType] = useState<'system' | 'user'>('system');
  const [promptText, setPromptText] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [selectedSubFeature, setSelectedSubFeature] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const translationPath = 'system_management.system_prompts';

  // Get available sub-features for the current feature
  const availableSubFeatures = useMemo(() => {
    return SUB_FEATURES_CONFIG[feature] || [];
  }, [feature]);

  const hasSubFeatures = availableSubFeatures.length > 0;
  const isEditingDefaultPrompt = prompt?.default_prompt === true;

  const defaultSnippets = [
    { label: t(`${translationPath}.snippets.tone_of_voice`), value: '%TONE_OF_VOICE%' },
    {
      label: t(`${translationPath}.snippets.customer_group_description`),
      value: '%CUSTOMER_GROUP_DESCRIPTION%',
    },
    { label: t(`${translationPath}.snippets.language`), value: '%LANGUAGE%' },
    { label: t(`${translationPath}.snippets.domain_name`), value: '%DOMAIN_NAME%' },
  ];

  // Define snippets per feature - memoized to avoid recreating on every render
  const snippetsByFeature: Record<string, Snippet[]> = useMemo(
    () => ({
      chat: [...defaultSnippets],
      social_media: [
        ...defaultSnippets,
        { label: t(`${translationPath}.snippets.platform`), value: '%PLATFORM%' },
        {
          label: t(`${translationPath}.snippets.add_generation_article_url`),
          value: '%ARTICLE_URL%',
        },
        {
          label: t(`${translationPath}.snippets.add_generation_article_title`),
          value: '%ARTICLE_TITLE%',
        },
        {
          label: t(`${translationPath}.snippets.add_generation_article_description`),
          value: '%ARTICLE_DESCRIPTION%',
        },
        {
          label: t(`${translationPath}.snippets.add_generation_article_content`),
          value: '%ARTICLE_CONTENT%',
        },
      ],
      ad_generation: [
        ...defaultSnippets,
        {
          label: t(`${translationPath}.snippets.add_generation_article_url`),
          value: '%ARTICLE_URL%',
        },
        {
          label: t(`${translationPath}.snippets.add_generation_article_title`),
          value: '%ARTICLE_TITLE%',
        },
        {
          label: t(`${translationPath}.snippets.add_generation_article_description`),
          value: '%ARTICLE_DESCRIPTION%',
        },
        {
          label: t(`${translationPath}.snippets.add_generation_article_content`),
          value: '%ARTICLE_CONTENT%',
        },
        {
          label: t(`${translationPath}.snippets.platform`),
          value: '%AD_PLATFORM%',
        },
      ],
      newsletter: [
        ...defaultSnippets,
        { label: t(`${translationPath}.snippets.urls_content`), value: '%URLS_CONTENT%' },
      ],
      product_description: [
        ...defaultSnippets,
        { label: t(`${translationPath}.snippets.product_details`), value: '%PRODUCT_DETAILS%' },
        { label: t(`${translationPath}.snippets.brand_text_amount`), value: '%BRAND_TEXT_AMOUNT%' },
        {
          label: t(`${translationPath}.snippets.category_text_amount`),
          value: '%CATEGORY_TEXT_AMOUNT%',
        },
        { label: t(`${translationPath}.snippets.brand_description`), value: '%BRAND_DESCRIPTION%' },
        {
          label: t(`${translationPath}.snippets.category_description`),
          value: '%CATEGORY_DESCRIPTION%',
        },
      ],
      category_description: [
        ...defaultSnippets,
        {
          label: t(`${translationPath}.snippets.category_page_content`),
          value: '%CATEGORY_PAGE_CONTENT%',
        },
        {
          label: t(`${translationPath}.snippets.content_length_guidelines`),
          value: '%CONTENT_LENGTH_GUIDELINES%',
        },
        {
          label: t(`${translationPath}.snippets.selected_brand_description`),
          value: '%SELECTED_BRAND_DESCRIPTION%',
        },

        {
          label: t(`${translationPath}.snippets.staff_favorites`),
          value: '%STAFF_FAVORITES%',
        },
        {
          label: t(`${translationPath}.snippets.top_picks`),
          value: '%TOP_PICKS%',
        },
      ],
    }),
    []
  );
  useEffect(() => {
    setName('');
    setPromptType('system');
    setPromptText('');
    setIsActive(true);
    setIsDefault(false);
    setSelectedSubFeature('');
  }, []);

  // Initialize form when prompt changes
  useEffect(() => {
    if (prompt) {
      setName(prompt.name);
      setPromptType(prompt.type);
      setPromptText(prompt.content);
      setIsActive(prompt.is_active);
      setIsDefault(prompt.default_prompt);
      setSelectedSubFeature(prompt.sub_feature || '');
    } else {
      setName('');
      setPromptType('system');
      setPromptText('');
      setIsActive(true);
      setIsDefault(false);
      setSelectedSubFeature('');
    }
  }, [prompt]);

  const handleSnippetClick = useCallback(
    (snippet: string) => {
      const textarea = document.querySelector('#prompt-edit-textarea') as HTMLTextAreaElement;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const text = promptText;
        const before = text.substring(0, start);
        const after = text.substring(end);
        const newText = before + snippet + after;
        setPromptText(newText);

        // Set cursor position after inserted snippet
        setTimeout(() => {
          textarea.focus();
          textarea.setSelectionRange(start + snippet.length, start + snippet.length);
        }, 0);
      }
    },
    [promptText]
  );

  const getCurrentSnippets = useCallback(() => {
    return snippetsByFeature[feature] || [];
  }, [feature, snippetsByFeature]);

  const handleSave = async () => {
    if (!name.trim()) {
      showNotification(t(`${translationPath}.name_required`), 'error');
      return;
    }

    if (!promptText.trim()) {
      showNotification(t(`${translationPath}.content_required`), 'error');
      return;
    }

    if (hasSubFeatures && !selectedSubFeature && !isEditingDefaultPrompt) {
      showNotification(t(`${translationPath}.sub_feature_required`), 'error');
      return;
    }

    setIsSaving(true);
    try {
      if (prompt) {
        // Updating existing prompt
        if (prompt.default_prompt) {
          // For default prompts, only update name and content
          await updateDefaultPrompt(prompt.id, {
            name: name.trim(),
            content: promptText.trim(),
            is_active: isActive,
          });
        } else {
          // For non-default prompts, update all fields
          const promptData: SaveSystemPromptParams = {
            name: name.trim(),
            type: promptType,
            content: promptText.trim(),
            feature,
            sub_feature: selectedSubFeature === '' ? null : selectedSubFeature,
            default_prompt: isDefault,
            is_active: isActive,
          };
          await updatePrompt(prompt.id, promptData);
        }
        showNotification(t(`${translationPath}.update_success`), 'success');
      } else {
        // Creating new prompt
        const promptData: SaveSystemPromptParams = {
          name: name.trim(),
          type: promptType,
          content: promptText.trim(),
          feature,
          sub_feature: selectedSubFeature === '' ? null : selectedSubFeature,
          default_prompt: isDefault,
          is_active: isActive,
        };
        await savePrompt(promptData);
        showNotification(t(`${translationPath}.create_success`), 'success');
      }

      onSave();
      onClose();
    } catch (error: any) {
      console.error('Error saving prompt:', error);

      // Extract error message from API response
      let errorMessage = t(`${translationPath}.save_error`);
      if (error?.message) {
        errorMessage = error.message;
      }

      showNotification(errorMessage, 'error', 10000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (!isSaving) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {prompt ? t(`${translationPath}.edit_prompt`) : t(`${translationPath}.create_prompt`)}
      </DialogTitle>
      <DialogContent>
        <div className="space-y-4 pt-4">
          <TextField
            fullWidth
            label={t(`${translationPath}.name`)}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />

          <FormControl fullWidth disabled={isEditingDefaultPrompt}>
            <InputLabel id="prompt-type-label">{t(`${translationPath}.prompt_type`)}</InputLabel>
            <Select
              labelId="prompt-type-label"
              value={promptType}
              label={t(`${translationPath}.prompt_type`)}
              onChange={(e) => setPromptType(e.target.value as 'system' | 'user')}
              disabled={isEditingDefaultPrompt}
            >
              <MenuItem value="system">{t(`${translationPath}.system_prompt`)}</MenuItem>
              <MenuItem value="user">{t(`${translationPath}.user_prompt`)}</MenuItem>
            </Select>
          </FormControl>

          {hasSubFeatures && (
            <FormControl fullWidth disabled={isEditingDefaultPrompt}>
              <InputLabel id="sub-feature-label">{t(`${translationPath}.sub_feature`)}</InputLabel>
              <Select
                labelId="sub-feature-label"
                value={selectedSubFeature}
                label={t(`${translationPath}.sub_feature`)}
                onChange={(e) => setSelectedSubFeature(e.target.value)}
                required={hasSubFeatures}
                disabled={isEditingDefaultPrompt}
              >
                {availableSubFeatures.map((sf) => (
                  <MenuItem key={sf} value={sf}>
                    {t(`${translationPath}.sub_features.${sf}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <div className="flex space-x-4">
            <FormControlLabel
              control={
                <Checkbox checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              }
              label={t(`${translationPath}.is_active`)}
            />
            <FormControlLabel
              control={<Checkbox checked={isDefault} disabled={true} />}
              disabled={true}
              label={t(`${translationPath}.is_default`)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t(`${translationPath}.prompt_content`)}
            </label>

            {/* Custom textarea container with snippets at bottom */}
            <div className="relative border border-gray-300 rounded-md overflow-hidden">
              <textarea
                id="prompt-edit-textarea"
                className="w-full p-3 border-0 outline-none"
                rows={12}
                value={promptText}
                onChange={(e) => setPromptText(e.target.value)}
                placeholder={t(`${translationPath}.prompt_placeholder`)}
                style={{ minHeight: '200px' }}
              />

              {/* Snippets section at bottom of textarea */}
              <div className="border-t border-gray-200 bg-gray-50 p-2">
                <div className="flex flex-wrap gap-1">
                  {getCurrentSnippets().map((snippet) => (
                    <Chip
                      key={snippet.value}
                      label={snippet.label}
                      onClick={() => handleSnippetClick(snippet.value)}
                      clickable
                      variant="outlined"
                      size="small"
                      className="cursor-pointer hover:bg-blue-50 text-xs"
                      sx={{
                        height: '24px',
                        fontSize: '0.75rem',
                        '& .MuiChip-label': {
                          px: 1,
                        },
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving}>
          {isSaving ? t('common.saving') : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PromptEditModal;
